import { CommonModule, TitleCasePipe } from '@angular/common';
import { ModuleWithProviders, NgModule, Optional, Renderer2, RendererFactory2 } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PblNformMaterialModule } from '@pebula/nform-material';
import { PblNgridModule } from '@pebula/ngrid';
import { PblNgridMaterialModule } from '@pebula/ngrid-material';
import { PblNgridBlockUiModule } from '@pebula/ngrid/block-ui';
import { PblNgridClipboardPluginModule } from '@pebula/ngrid/clipboard';
import { PblNgridDetailRowModule } from '@pebula/ngrid/detail-row';
import { PblNgridDragModule } from '@pebula/ngrid/drag';
import { PblNgridInfiniteScrollModule } from '@pebula/ngrid/infinite-scroll';
import { PblNgridStatePluginModule } from '@pebula/ngrid/state';
import { PblNgridStickyModule } from '@pebula/ngrid/sticky';
import { PblNgridTargetEventsModule } from '@pebula/ngrid/target-events';
import { PblNgridTransposeModule } from '@pebula/ngrid/transpose';
import { SgMaterialAllModule } from '@sg/material-all';
import { SgUiKitModule } from '@sg/ui-kit';
import { SgToNoCasePipe } from '@sg/ui-kit/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SgAsyncGuiDialogBoxComponent } from './async-gui-dialog-box/async-gui-dialog-box.component';
import { MatDrawerAuxRouteOutletDirective } from './aux-route-bridge.extension';
import { SgComponentHighlightComponent } from './component-highlight';
import {
  CookiePolicyComponent,
  SgActionConfirmationComponent,
  SgBackgroundIconComponent,
  SgCommonSgTableColumnsComponent,
  SgDataBoxComponent,
  SgDetailItemCardComponent,
  SgDialogComponent,
  SgGeneralErrorComponent,
  SgScrollableComponent,
  SgTableActionRowComponent,
} from './components';
import { SgCallMeComponent } from './components/call-me/call-me.component';
import { SgCounterPartyLogoComponent } from './components/counter-party-logo/counter-party-logo.component';
import { SgEntitySelectAutoCompleteComponent } from './components/entity-select-auto-complete';
import { SgCustomEntitySelectAutoCompleteComponent } from './components/entity-select-auto-complete/custom-entity-select-auto-complete/custom-entity-select-auto-complete.component';
import {
  CustomMaxDirective,
  CustomMinDirective,
  DataTestDirective,
  DisableCopyPasteDirective,
  DisableFormDirective,
  DragDropFileDirective,
  PreventFocusOnClick,
  SgTextOverflowTooltipDirective,
} from './directives';
import { SgStoreSearchParamsDirective } from './dynamic-form-extensions/store-search-params.directive';
import { SgLocateMatchCellsModule } from './locate-match-cells/locate-match-cells.module';
import { HeaderActionsMenuComponent } from './header-actions-menu/header-actions-menu.component';
import { SgNotificationsPanelComponent } from './notifications-panel/notifications-panel.component';
import { SgPageNotFoundComponent } from './page-not-found/page-not-found.component';
import { SgTranslateColumnPipe, SgCorporateEventLabelFormatterPipe } from './pipes';
import { SgRouterOutletCanDeactivateDirective } from './router-can-deactivate';
import { EnvironmentService } from './services';
import { SgTableExtensionModule } from './sg-table-extension.module';
import { SgStoreTableStateDirective } from './sg-table-extension.module/store-table-state/store-table-state.directive';
import {
  SgNavItemDefDirective,
  SgNavMenuComponent,
  SgNavMenuItemComponent,
  SgNavMenuTrigger,
  SgSidenavComponent,
  SgSidenavDirective,
} from './sidenav';
import { SgToasterComponent } from './toaster';
import { SgToasterDefDirective } from './toaster/toaster-def.directive';
import { SgTopNavComponent, SgTopNavElementDirective } from './top-nav';
import { SgYesNoClickConfirmPanel } from './yes-no-click-confirm-panel/yes-no-click-confirm-panel.component';

const importAndExport = [
  TranslateModule,
  SgMaterialAllModule,
  SgUiKitModule,
  PblNgridModule,
  PblNgridTargetEventsModule, // Must be imported before plugins that depend on it
  PblNgridDragModule.withDefaultTemplates(), // Drag plugin with default templates
  PblNgridTransposeModule,
  PblNgridStatePluginModule,
  PblNgridClipboardPluginModule,
  PblNgridInfiniteScrollModule,
  PblNgridMaterialModule,
  SgTableExtensionModule,
  PblNgridDetailRowModule,
  PblNgridBlockUiModule,
  PblNgridStickyModule,
  NgxSkeletonLoaderModule,
  SgTextOverflowTooltipDirective,
  SgLocateMatchCellsModule,
];

const declareAndExport = [
  SgSidenavComponent,
  SgNavItemDefDirective,
  SgSidenavDirective,
  SgNavMenuTrigger,
  SgNavMenuComponent,
  SgNavMenuItemComponent,
  SgTopNavElementDirective,
  SgTopNavComponent,
  SgNotificationsPanelComponent,
  SgYesNoClickConfirmPanel,
  SgAsyncGuiDialogBoxComponent,
  SgPageNotFoundComponent,
  SgCallMeComponent,
  SgStoreSearchParamsDirective,
  SgStoreTableStateDirective,
  CustomMinDirective,
  CustomMaxDirective,
  DataTestDirective,
  DisableCopyPasteDirective,
  DragDropFileDirective,
  PreventFocusOnClick,
  SgScrollableComponent,
  SgBackgroundIconComponent,
  SgGeneralErrorComponent,
  SgCommonSgTableColumnsComponent,
  SgTableActionRowComponent,
  SgDetailItemCardComponent,
  SgActionConfirmationComponent,
  CookiePolicyComponent,
  MatDrawerAuxRouteOutletDirective,
  SgTranslateColumnPipe,
  SgCorporateEventLabelFormatterPipe,
  SgRouterOutletCanDeactivateDirective,
  SgDataBoxComponent,
  SgComponentHighlightComponent,
  SgToasterDefDirective,
  HeaderActionsMenuComponent,
  SgEntitySelectAutoCompleteComponent,
  SgCustomEntitySelectAutoCompleteComponent,
  SgDialogComponent,
  SgCounterPartyLogoComponent,
  DisableFormDirective,
];

@NgModule({
  imports: [CommonModule, RouterModule, importAndExport, PblNformMaterialModule, ReactiveFormsModule],
  declarations: [declareAndExport, SgToasterComponent],
  exports: [declareAndExport, importAndExport, PblNformMaterialModule],
  providers: [
    TitleCasePipe,
    SgToNoCasePipe, // we need these for `SgTranslateColumnPipe`
    { provide: Renderer2, useFactory: (f: RendererFactory2) => f.createRenderer(null, null), deps: [RendererFactory2] },
  ],
})
export class SgCommonModule {
  static forRoot(): ModuleWithProviders<SgCommonModule> {
    return {
      ngModule: SgCommonModule,
      providers: [...PblNformMaterialModule.forRoot().providers],
    };
  }

  constructor(@Optional() env: EnvironmentService) {
    if (!env) {
      throw new Error('EnvironmentService provider is not set, please make sure to attach the environment');
    }
  }
}
